import React from 'react';
import styled from 'styled-components';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const PrivacyPolicy = () => {
  return (
    <>
      <Navbar />
      <PageBanner>
        <BannerAccent />
        <BannerContent>
          <BreadCrumb>Home / Privacy Policy</BreadCrumb>
          <PageTitle>Privacy Policy</PageTitle>
          <GoldBar />
        </BannerContent>
      </PageBanner>

      <ContentSection>
        <ContentInner>
          <LastUpdated>Last updated: January 1, 2024</LastUpdated>

          <Block>
            <BlockTitle>Introduction</BlockTitle>
            <BlockText>
              Champion &amp; Champion Construction ("we," "our," or "us") is committed to protecting
              your privacy. This Privacy Policy explains how we collect, use, and safeguard your
              personal information when you visit our website or contact us about our services.
              By using our website, you agree to the practices described in this policy.
            </BlockText>
          </Block>

          <Block>
            <BlockTitle>Information We Collect</BlockTitle>
            <BlockText>We may collect the following types of information:</BlockText>
            <List>
              <ListItem>
                <strong>Contact Information:</strong> Your name, email address, and phone number
                when you submit our contact form or reach out to us directly.
              </ListItem>
              <ListItem>
                <strong>Project Details:</strong> Information about your construction or renovation
                project that you voluntarily provide when requesting a quote or consultation.
              </ListItem>
              <ListItem>
                <strong>Usage Data:</strong> Anonymous data about how you navigate our website,
                including pages visited and time spent, collected through Google Analytics.
              </ListItem>
            </List>
          </Block>

          <Block>
            <BlockTitle>How We Use Your Information</BlockTitle>
            <BlockText>
              The information we collect is used solely to serve you better. Specifically, we use it to:
            </BlockText>
            <List>
              <ListItem>Respond to your inquiries and provide project estimates</ListItem>
              <ListItem>Contact you about your construction or renovation project</ListItem>
              <ListItem>Improve our website and services based on usage patterns</ListItem>
              <ListItem>Send you relevant updates about your project (if applicable)</ListItem>
            </List>
            <BlockText style={{ marginTop: '16px' }}>
              We do <strong>not</strong> sell, trade, or rent your personal information to third parties.
            </BlockText>
          </Block>

          <Block>
            <BlockTitle>Google Analytics</BlockTitle>
            <BlockText>
              Our website uses Google Analytics to understand how visitors interact with our site.
              Google Analytics collects anonymous usage data such as pages viewed, time on site,
              and general geographic location. This data does not identify you personally.
              You can opt out of Google Analytics by installing the{' '}
              <InlineLink
                href="https://tools.google.com/dlpage/gaoptout"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google Analytics Opt-out Browser Add-on
              </InlineLink>.
            </BlockText>
          </Block>

          <Block>
            <BlockTitle>Cookies</BlockTitle>
            <BlockText>
              Our website may use cookies to enhance your browsing experience. Cookies are small
              files stored on your device that help us remember your preferences and understand
              how you use our site. You can disable cookies through your browser settings;
              however, some features of the site may not function properly without them.
            </BlockText>
          </Block>

          <Block>
            <BlockTitle>Data Security</BlockTitle>
            <BlockText>
              We take reasonable precautions to protect your personal information from unauthorized
              access, disclosure, or misuse. Contact form submissions are transmitted securely
              via EmailJS. However, no method of transmission over the internet is 100% secure,
              and we cannot guarantee absolute security.
            </BlockText>
          </Block>

          <Block>
            <BlockTitle>Third-Party Links</BlockTitle>
            <BlockText>
              Our website may contain links to third-party websites, such as our Instagram profile.
              We are not responsible for the privacy practices or content of those sites. We encourage
              you to review the privacy policies of any third-party websites you visit.
            </BlockText>
          </Block>

          <Block>
            <BlockTitle>Children's Privacy</BlockTitle>
            <BlockText>
              Our website is not directed at children under the age of 13. We do not knowingly
              collect personal information from children. If you believe a child has provided us
              with personal information, please contact us and we will promptly delete it.
            </BlockText>
          </Block>

          <Block>
            <BlockTitle>Your Rights</BlockTitle>
            <BlockText>You have the right to:</BlockText>
            <List>
              <ListItem>Request access to the personal information we hold about you</ListItem>
              <ListItem>Request correction or deletion of your personal information</ListItem>
              <ListItem>Opt out of any future communications from us at any time</ListItem>
            </List>
            <BlockText style={{ marginTop: '16px' }}>
              To exercise any of these rights, please contact us using the information below.
            </BlockText>
          </Block>

          <Block>
            <BlockTitle>Changes to This Policy</BlockTitle>
            <BlockText>
              We may update this Privacy Policy from time to time. Any changes will be posted
              on this page with an updated "Last updated" date. We encourage you to review this
              policy periodically to stay informed about how we protect your information.
            </BlockText>
          </Block>

          <ContactBox>
            <ContactBoxTitle>Questions About This Policy?</ContactBoxTitle>
            <ContactBoxText>
              If you have any questions or concerns about this Privacy Policy, please reach out:
            </ContactBoxText>
            <ContactDetail>
              <strong>Champion &amp; Champion Construction</strong>
            </ContactDetail>
            <ContactDetail>
              <ContactLink href="tel:+18188095041">1 (818) 809-5041</ContactLink>
            </ContactDetail>
            <ContactDetail>
              <ContactLink href="mailto:championbuilding12@gmail.com">
                championbuilding12@gmail.com
              </ContactLink>
            </ContactDetail>
            <ContactDetail>Los Angeles, California</ContactDetail>
          </ContactBox>
        </ContentInner>
      </ContentSection>

      <Footer />
    </>
  );
};

/* ── Styles ── */

const PageBanner = styled.div`
  position: relative;
  background: #111827;
  padding: 72px 32px 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(
      -45deg,
      transparent,
      transparent 20px,
      rgba(255, 255, 255, 0.015) 20px,
      rgba(255, 255, 255, 0.015) 40px
    );
  }
`;

const BannerAccent = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  background: #c89432;
`;

const BannerContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
`;

const BreadCrumb = styled.p`
  font-family: "Poppins", sans-serif;
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #c89432;
  margin-bottom: 12px;
`;

const PageTitle = styled.h1`
  font-family: "Poppins", sans-serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  color: #fff;
  letter-spacing: 1px;
  margin-bottom: 20px;
`;

const GoldBar = styled.div`
  width: 56px;
  height: 4px;
  background: #c89432;
  border-radius: 2px;
  margin: 0 auto;
`;

const ContentSection = styled.section`
  background: #fff;
  padding: 80px 32px;
  @media (max-width: 768px) {
    padding: 52px 20px;
  }
`;

const ContentInner = styled.div`
  max-width: 820px;
  margin: 0 auto;
`;

const LastUpdated = styled.p`
  font-family: "Poppins", sans-serif;
  font-size: 0.8rem;
  color: #9ca3af;
  margin-bottom: 48px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e5e7eb;
`;

const Block = styled.div`
  margin-bottom: 44px;
`;

const BlockTitle = styled.h2`
  font-family: "Poppins", sans-serif;
  font-size: 1.15rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 12px;
  padding-left: 14px;
  border-left: 3px solid #c89432;
`;

const BlockText = styled.p`
  font-family: "Poppins", sans-serif;
  font-size: 0.95rem;
  color: #4b5563;
  line-height: 1.8;
`;

const List = styled.ul`
  margin-top: 12px;
  padding-left: 8px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ListItem = styled.li`
  font-family: "Poppins", sans-serif;
  font-size: 0.95rem;
  color: #4b5563;
  line-height: 1.7;
  padding-left: 20px;
  position: relative;
  list-style: none;
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 10px;
    width: 6px;
    height: 6px;
    background: #c89432;
    border-radius: 50%;
  }
`;

const InlineLink = styled.a`
  color: #c89432;
  font-weight: 500;
  transition: opacity 0.2s;
  &:hover { opacity: 0.75; }
`;

const ContactBox = styled.div`
  margin-top: 56px;
  padding: 36px 40px;
  background: #f9fafb;
  border-radius: 8px;
  border-left: 4px solid #c89432;
  @media (max-width: 600px) {
    padding: 28px 24px;
  }
`;

const ContactBoxTitle = styled.h3`
  font-family: "Poppins", sans-serif;
  font-size: 1.1rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
`;

const ContactBoxText = styled.p`
  font-family: "Poppins", sans-serif;
  font-size: 0.9rem;
  color: #6b7280;
  margin-bottom: 16px;
`;

const ContactDetail = styled.p`
  font-family: "Poppins", sans-serif;
  font-size: 0.9rem;
  color: #374151;
  line-height: 1.9;
`;

const ContactLink = styled.a`
  color: #c89432;
  font-weight: 500;
  transition: opacity 0.2s;
  &:hover { opacity: 0.75; }
`;

export default PrivacyPolicy;
